import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApiService } from '../../../lib/api.service';
import { ModalService } from '../../../lib/modal.service';

import { UtilityService } from 'app/lib/utility.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-add-resources',
    templateUrl: './add-resources.component.html',
    styleUrls: ['./add-resources.component.scss'],
    standalone: false,
})
export class AddResourcesComponent implements OnInit {
    resourceForm: FormGroup;
    isloaded = false;
    resources: any;
    urlPattern: String;
    isSubmitted = false;
    resourcesList = [];
    resourcePopups: any;

    constructor(
        public dialogRef: MatDialogRef<AddResourcesComponent>,
        private formBuilder: FormBuilder,
        private api: ApiService,
        private modalService: ModalService,
        private utility: UtilityService,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private translate: TranslateService
    ) {
        dialogRef.disableClose = true;
        this.resources = data.data;
        this.urlPattern = this.lenientUrlPattern();
        this.constructForm();
    }

    ngOnInit() {
        this.translate.get('resources.popups').subscribe((res: any) => {
            this.resourcePopups = res;
        });
    }

    /**
     * Construct form
     */
    constructForm() {
        this.resourceForm = this.formBuilder.group(
            {
                id: [this.resources.id || null],
                title: [this.resources.title || '', [Validators.required]],
                contact: [
                    this.resources.contact || '',
                    Validators.pattern(this.contactPattern()),
                ], // phone number
                alternateContact: [
                    this.resources.alternateContact || '',
                    Validators.pattern(this.utility.emailValidationPattern()),
                ],
                websiteTitle: [this.resources.websiteTitle || ''],
                website: [
                    this.resources.website || '',
                    Validators.pattern(this.lenientUrlPattern()),
                ],
                address: [this.resources.address || ''],
                internal: [this.resources.internal || ''],
                description: [this.resources.description || ''],
                active: [this.resources.active === 0 ? 0 : 1],
            },
            {
                validators: this.utility.atLeastOneValidator([
                    'contact',
                    'website',
                    'address',
                    'alternateContact',
                ]),
            }
        );
    }

    /**
     * URL pattern that accepts bare domains ("example.com", "www.x.io")
     * in addition to fully-qualified URLs ("https://example.com/path").
     * The doSave handlers normalize the value with `normalizeWebsite()`
     * before submitting.
     */
    private lenientUrlPattern(): string {
        return '^(https?:\\/\\/)?(www\\.)?([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\\.)+[a-zA-Z]{2,}(:\\d+)?(\\/[^\\s]*)?$';
    }
    private contactPattern(): any {
        return /^[0-9()[\]+\-\s]+$/;
    }

    /**
     * Auto-prepend `https://` to a website value when the admin typed a
     * bare domain. Anything that already looks like it has a protocol is
     * left alone.
     */
    private normalizeWebsite(value: string): string {
        if (!value) {
            return value;
        }
        const trimmed = value.trim();
        if (/^https?:\/\//i.test(trimmed)) {
            return trimmed;
        }
        return 'https://' + trimmed;
    }

    // Save the form data
    doSave() {
        if (this.resourceForm.invalid) {
            return;
        }
        this.modalService
            .showConfirmation(
                this.resourcePopups.saveConfirmationTitle,
                this.resourcePopups.saveConfirmationMsg
            )
            .afterClosed()
            .subscribe(result => {
                if (result) {
                    this.saveConfirmation();
                }
            });
    }

    saveConfirmation() {
        this.isSubmitted = true;
        const formData = this.resourceForm.value;
        this.resourceForm.value.active = this.resourceForm.value.active ? 1 : 0;
        formData.website = this.normalizeWebsite(formData.website);
        formData.resourcesetID = this.resources.resourceSetId;
        formData.organization = this.resources.organizationId;
        this.resourcesList.push(formData);
        if (this.resourceForm.valid) {
            this.dialogRef.close(this.resourcesList);
        }
    }
    // Save the form data and add another resource
    doSaveAndAdd() {
        if (this.resourceForm.invalid) {
            return;
        }
        this.modalService
            .showConfirmation(
                this.resourcePopups.saveAndAddConfirmationTitle,
                this.resourcePopups.saveAndAddAnother
            )
            .afterClosed()
            .subscribe(result => {
                if (result) {
                    this.saveAndAddConfirmation();
                }
            });
    }

    saveAndAddConfirmation() {
        this.isSubmitted = true;
        const formData = this.resourceForm.value;
        this.resourceForm.value.active = this.resourceForm.value.active ? 1 : 0;
        formData.website = this.normalizeWebsite(formData.website);
        formData.resourcesetID = this.resources.resourceSetId;
        formData.organization = this.resources.organizationId;
        this.resourcesList.push(formData);
        if (this.resourceForm.valid) {
            this.resourceForm.reset();
            this.isSubmitted = false;
            // reset form value
            this.resourceForm.setValue({
                id: null,
                title: '',
                contact: '',
                alternateContact: '',
                websiteTitle: '',
                website: '',
                address: '',
                internal: '',
                description: '',
                active: 1,
            });
        }
        this.modalService.showAlert(
            this.resourcePopups.success,
            this.resourcePopups.resourceSaveSuccessMsg
        );
    }

    // Close the Dialog
    onClose() {
        if (this.resourceForm.dirty || this.resourcesList.length > 0) {
            this.modalService
                .showConfirmation(
                    'Discard Changes?',
                    'Are you sure you want to close it without saving the changes?'
                )
                .afterClosed()
                .subscribe(result => {
                    if (!result) {
                        return;
                    }
                    this.dialogRef.close(this.resourcesList);
                });
        } else {
            this.dialogRef.close();
        }
    }
}
